import React, { MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Communication } from 'icons/untitledui';
import FilledDocumentIcon from 'form/assets/filledForm.svg?react';
import routesConfig from 'constants/routesConfig.json';
import { FormPageLocationState } from 'form/types';
import type { FormInstance } from 'types/step';
import { useNoteFormOpen } from 'shiftNotes/components/NotesStack/NoteFormOpenContext';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import * as Styled from 'shiftNotes/styles/NotesStack.style';

interface NoteFormCardProps {
  noteId: string;
  authorName: string;
  time: string;
  formInstance: FormInstance;
}

export default function NoteFormCard({
  noteId,
  authorName,
  time,
  formInstance,
}: NoteFormCardProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const history = useHistory();
  const noteFormOpen = useNoteFormOpen();
  const shiftNotesCapture = usePosthogCapture();

  if (!formInstance.file) return null;

  const title = formInstance.form_template?.document_translation?.title ?? '';
  const subtitle =
    formInstance.generated_text
      ?.split('\n')
      .find((line) => line.trim())
      ?.trim() ?? '';

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    shiftNotesCapture.noteFormCardOpened({
      shift_note_id: noteId,
      form_instance_id: formInstance.id,
    });
    if (noteFormOpen) {
      noteFormOpen.onOpenForm({
        formInstance,
        isCompleted: true,
        origin: 'direct',
        noteId,
      });
      return;
    }
    const locationState: FormPageLocationState = {
      formInstance,
      isCompleted: true,
      origin: 'direct',
    };
    history.push(
      `${routesConfig.SHIFT_NOTES_PAGE}/${noteId}/form`,
      locationState
    );
  };

  return (
    <Styled.Card>
      <Styled.Header>
        <Styled.FormCardAuthorStack>
          <Styled.FormCardAuthor>{authorName}</Styled.FormCardAuthor>
          <Styled.FormCardTime>{time}</Styled.FormCardTime>
        </Styled.FormCardAuthorStack>
      </Styled.Header>
      <Styled.FormCardPill
        type="button"
        onClick={handleOpen}
        aria-label={t('noteFormCard.openForm', { title })}
      >
        <Styled.FormCardIcon aria-hidden="true">
          <FilledDocumentIcon />
        </Styled.FormCardIcon>
        <Styled.FormCardText>
          <Styled.FormCardTitle>{title}</Styled.FormCardTitle>
          {subtitle && (
            <Styled.FormCardSubtitle>{subtitle}</Styled.FormCardSubtitle>
          )}
        </Styled.FormCardText>
        <Styled.FormCardSendIcon aria-hidden="true">
          <Communication.Send01 />
        </Styled.FormCardSendIcon>
      </Styled.FormCardPill>
    </Styled.Card>
  );
}
